import { getConsentGate } from 'lib/consent/gate';
import { TRACKERS } from 'lib/analytics/trackers';

/** Custom GTM/dataLayer event for Performance Lab Typeform completions. */
export const PERFORMANCE_LAB_TYPEFORM_SUBMIT_EVENT =
  'performance_lab_form_submit';

type TypeformSubmitEvent = {
  formId: string;
  responseId: string;
};

/**
 * Host-page conversion signal for a PL Typeform submit — the lead itself happens
 * in a cross-origin iframe, so GTM triggers the Google Ads tag off this event.
 */
export function trackPerformanceLabTypeformSubmit(
  event: TypeformSubmitEvent,
): void {
  if (typeof window === 'undefined') return;
  if (!leadAdsAllowed()) return;

  const payload = {
    event: PERFORMANCE_LAB_TYPEFORM_SUBMIT_EVENT,
    form_id: event.formId,
    response_id: event.responseId,
    form_type: 'typeform',
  };

  try {
    const analyticsWindow = window as Window & {
      dataLayer?: Record<string, unknown>[];
    };
    analyticsWindow.dataLayer ??= [];
    analyticsWindow.dataLayer.push(payload);

    window.gtag?.('event', PERFORMANCE_LAB_TYPEFORM_SUBMIT_EVENT, {
      form_id: event.formId,
      response_id: event.responseId,
      form_type: 'typeform',
      send_to: TRACKERS.googleAds.id,
    });
  } catch {
    // Tracking must never break the form UX.
  }
}

const E164_PHONE = /^\+[1-9]\d{9,14}$/;
const LEGACY_CONSENT_STORAGE_KEY = 'COOKIE_CONSENT';

/** Next stack uses the ads gate. Older stores keep that gate open, so Accept is read directly. */
function leadAdsAllowed(): boolean {
  const gate = getConsentGate();
  if (gate.stack === 'next') return gate.ads;
  try {
    return (
      JSON.parse(
        window.localStorage.getItem(LEGACY_CONSENT_STORAGE_KEY) ?? 'null',
      ) === true
    );
  } catch {
    return false;
  }
}

/**
 * Google enhanced conversions for leads. Values stay unhashed.
 * GTM hashes them. The Typeform iframe does not expose an email, so this
 * only runs from the in-page lead form.
 */
export function trackEnhancedConversionLead(input: {
  email: string;
  phone?: string;
}): void {
  if (typeof window === 'undefined') return;
  if (!leadAdsAllowed()) return;
  const email = input.email.trim();
  if (!email) return;
  const userData: { email: string; phone_number?: string } = { email };
  const phone = input.phone?.trim() ?? '';
  if (E164_PHONE.test(phone)) userData.phone_number = phone;

  try {
    const analyticsWindow = window as Window & {
      dataLayer?: Record<string, unknown>[];
    };
    analyticsWindow.dataLayer ??= [];
    analyticsWindow.dataLayer.push({
      event: 'ec_form_submit',
      user_data: userData,
    });
  } catch {
    // Tracking must never break the form UX.
  }
}
